/**
 * EducationEngine - Manages educational content and learning features
 */

export class EducationEngine {
  constructor() {
    this.biomeService = null;
    this.offlineContentManager = null;
    this.userAnalytics = null;
    this.apiService = null;
    this.learningPaths = {};
    this.achievements = [];
    this.userProgress = {
      biomesExplored: new Set(),
      quizzesTaken: new Set(),
      correctAnswers: 0,
      totalQuestions: 0,
      explorationTime: 0,
      achievements: new Set()
    };
    this.sessionStartTime = Date.now();
  }

  /**
   * Set biome service reference
   */
  setBiomeService(biomeService) {
    this.biomeService = biomeService;
  }

  /**
   * Set offline content manager reference
   */
  setOfflineContentManager(manager) {
    this.offlineContentManager = manager;
  }

  /**
   * Set user analytics reference
   */
  setUserAnalytics(analytics) {
    this.userAnalytics = analytics;
  }

  /**
   * Set API service reference
   */
  setAPIService(apiService) {
    this.apiService = apiService;
  }

  /**
   * Load educational content
   */
  async loadContent() {
    try {
      this.loadLearningPaths();
      this.loadAchievements();
      this.loadUserProgress();

      console.log('Educational content loaded successfully');
    } catch (error) {
      throw new Error(`Failed to load educational content: ${error.message}`);
    }
  }

  /**
   * Define learning paths for different educational goals
   */
  loadLearningPaths() {
    this.learningPaths = {
      'beginner': {
        title: 'Introduction to Biomes',
        description: 'Learn the basics of Earth\'s major biomes',
        sequence: ['Tropical', 'Temperate', 'Desert', 'Polar'],
        objectives: [
          'Identify the four major biome types',
          'Understand basic climate characteristics',
          'Learn about adaptations in each biome'
        ]
      },
      'climate': {
        title: 'Climate and Weather Patterns',
        description: 'Explore how climate shapes different biomes',
        sequence: ['Tropical', 'Desert', 'Temperate', 'Polar', 'Tundra'],
        objectives: [
          'Understand temperature and precipitation patterns',
          'Learn about seasonal variations',
          'Connect latitude to climate zones'
        ]
      },
      'biodiversity': {
        title: 'Life in Different Biomes',
        description: 'Discover the amazing diversity of life on Earth',
        sequence: ['Tropical', 'Aquatic', 'Temperate', 'Grassland', 'Desert'],
        objectives: [
          'Explore adaptations of plants and animals',
          'Learn about food webs and ecosystems',
          'Understand biodiversity hotspots'
        ]
      },
      'conservation': {
        title: 'Environmental Protection',
        description: 'Learn about conservation and environmental challenges',
        sequence: ['Tropical', 'Aquatic', 'Mountain', 'Polar'],
        objectives: [
          'Understand environmental threats',
          'Learn about conservation efforts',
          'Explore sustainable practices'
        ]
      }
    };
  }

  /**
   * Define achievement system
   */
  loadAchievements() {
    this.achievements = [
      {
        id: 'first_explorer',
        title: 'First Explorer',
        description: 'Explore your first biome',
        icon: 'EXP',
        condition: () => this.userProgress.biomesExplored.size >= 1
      },
      {
        id: 'biome_collector',
        title: 'Biome Collector',
        description: 'Explore all major biome types',
        icon: 'COL',
        condition: () => this.userProgress.biomesExplored.size >= 4
      },
      {
        id: 'quiz_master',
        title: 'Quiz Master',
        description: 'Answer 10 quiz questions correctly',
        icon: 'QUIZ',
        condition: () => this.userProgress.correctAnswers >= 10
      },
      {
        id: 'dedicated_learner',
        title: 'Dedicated Learner',
        description: 'Spend 10 minutes exploring',
        icon: 'TIME',
        condition: () => this.userProgress.explorationTime >= 600000 // 10 minutes
      },
      {
        id: 'world_traveler',
        title: 'World Traveler',
        description: 'Explore biomes on different continents',
        icon: 'TRAV',
        condition: () => this.checkContinentExploration()
      },
      {
        id: 'perfect_score',
        title: 'Perfect Score',
        description: 'Get 5 quiz questions right in a row',
        icon: 'ACE',
        condition: () => this.checkPerfectStreak()
      }
    ];
  }

  /**
   * Load user progress from localStorage
   */
  loadUserProgress() {
    const savedProgress = localStorage.getItem('landgrid-progress');
    if (savedProgress) {
      try {
        const parsed = JSON.parse(savedProgress);
        this.userProgress = {
          ...this.userProgress,
          ...parsed,
          biomesExplored: new Set(parsed.biomesExplored || []),
          quizzesTaken: new Set(parsed.quizzesTaken || []),
          achievements: new Set(parsed.achievements || [])
        };
      } catch (error) {
        console.warn('Failed to load user progress:', error);
      }
    }
  }

  /**
   * Save user progress to localStorage
   */
  saveUserProgress() {
    const progressToSave = {
      ...this.userProgress,
      biomesExplored: Array.from(this.userProgress.biomesExplored),
      quizzesTaken: Array.from(this.userProgress.quizzesTaken),
      achievements: Array.from(this.userProgress.achievements),
      lastSaved: Date.now()
    };

    localStorage.setItem('landgrid-progress', JSON.stringify(progressToSave));
  }

  /**
   * Record biome exploration
   */
  recordBiomeExploration(biome, _lat, _lon) {
    this.userProgress.biomesExplored.add(biome);
    this.updateExplorationTime();

    // Check for new achievements
    this.checkAchievements();

    // Save progress
    this.saveUserProgress();

    return {
      isNewBiome: !this.userProgress.biomesExplored.has(biome),
      totalExplored: this.userProgress.biomesExplored.size
    };
  }

  /**
   * Record quiz attempt
   */
  recordQuizAnswer(biome, question, isCorrect, _timeSpent = 0) {
    const quizId = `${biome}_${question}`;
    this.userProgress.quizzesTaken.add(quizId);
    this.userProgress.totalQuestions++;

    if (isCorrect) {
      this.userProgress.correctAnswers++;
    }

    // Check for achievements
    this.checkAchievements();

    // Save progress
    this.saveUserProgress();

    return {
      totalCorrect: this.userProgress.correctAnswers,
      accuracy: (this.userProgress.correctAnswers / this.userProgress.totalQuestions) * 100,
      questionsAnswered: this.userProgress.totalQuestions
    };
  }

  /**
   * Update exploration time
   */
  updateExplorationTime() {
    this.userProgress.explorationTime = Date.now() - this.sessionStartTime;
  }

  /**
   * Check for newly earned achievements
   */
  checkAchievements() {
    const newAchievements = [];

    for (const achievement of this.achievements) {
      if (!this.userProgress.achievements.has(achievement.id) && achievement.condition()) {
        this.userProgress.achievements.add(achievement.id);
        newAchievements.push(achievement);
      }
    }

    // Notify about new achievements
    newAchievements.forEach(achievement => {
      this.notifyAchievement(achievement);
    });

    return newAchievements;
  }

  /**
   * Notify user of new achievement
   */
  notifyAchievement(achievement) {
    // Create achievement notification
    const notification = document.createElement('div');
    notification.className = 'achievement-notification';
    notification.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: linear-gradient(135deg, #fbbf24, #f59e0b);
      color: white;
      padding: 20px;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
      z-index: 10000;
      text-align: center;
      font-size: 16px;
      min-width: 300px;
      animation: achievementPop 0.6s ease-out;
    `;

    notification.innerHTML = `
      <div style="font-size: 32px; margin-bottom: 8px;">${achievement.icon}</div>
      <div style="font-weight: bold; margin-bottom: 4px;">Achievement Unlocked!</div>
      <div style="font-size: 18px; font-weight: 600; margin-bottom: 4px;">${achievement.title}</div>
      <div style="font-size: 14px; opacity: 0.9;">${achievement.description}</div>
    `;

    // Add animation keyframes if not already present
    if (!document.querySelector('#achievement-styles')) {
      const styles = document.createElement('style');
      styles.id = 'achievement-styles';
      styles.textContent = `
        @keyframes achievementPop {
          0% { transform: translate(-50%, -50%) scale(0.3); opacity: 0; }
          50% { transform: translate(-50%, -50%) scale(1.1); }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(notification);

    // Remove after delay
    setTimeout(() => {
      notification.style.animation = 'achievementPop 0.6s ease-out reverse';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 600);
    }, 3000);
  }

  /**
   * Get personalized learning suggestions
   */
  getLearningSuggestions() {
    const suggestions = [];

    // Suggest unexplored biomes
    const allBiomes = this.biomeService ? this.biomeService.getAllBiomes() : [];
    const unexploredBiomes = allBiomes.filter(biome =>
      !this.userProgress.biomesExplored.has(biome)
    );

    if (unexploredBiomes.length > 0) {
      suggestions.push({
        type: 'explore',
        title: 'Discover New Biomes',
        description: `You haven't explored ${unexploredBiomes.join(', ')} yet!`,
        action: 'Explore these biomes to learn about different ecosystems.',
        priority: 'high'
      });
    }

    // Suggest quiz practice if accuracy is low
    const accuracy = this.userProgress.totalQuestions > 0 ?
      (this.userProgress.correctAnswers / this.userProgress.totalQuestions) * 100 : 0;

    if (this.userProgress.totalQuestions > 3 && accuracy < 70) {
      suggestions.push({
        type: 'quiz',
        title: 'Practice Quiz Questions',
        description: `Your quiz accuracy is ${accuracy.toFixed(1)}%. Practice makes perfect!`,
        action: 'Take more quizzes to improve your understanding.',
        priority: 'medium'
      });
    }

    // Suggest learning paths
    if (this.userProgress.biomesExplored.size >= 2) {
      suggestions.push({
        type: 'path',
        title: 'Follow a Learning Path',
        description: 'Ready for structured learning?',
        action: 'Try one of our guided learning paths to deepen your knowledge.',
        priority: 'low'
      });
    }

    return suggestions;
  }

  /**
   * Get progress statistics
   */
  getProgressStats() {
    const allBiomes = this.biomeService ? this.biomeService.getAllBiomes() : [];

    return {
      biomesExplored: {
        count: this.userProgress.biomesExplored.size,
        total: allBiomes.length,
        percentage: allBiomes.length > 0 ?
          Math.round((this.userProgress.biomesExplored.size / allBiomes.length) * 100) : 0
      },
      quizPerformance: {
        correct: this.userProgress.correctAnswers,
        total: this.userProgress.totalQuestions,
        accuracy: this.userProgress.totalQuestions > 0 ?
          Math.round((this.userProgress.correctAnswers / this.userProgress.totalQuestions) * 100) : 0
      },
      achievements: {
        earned: this.userProgress.achievements.size,
        total: this.achievements.length,
        percentage: Math.round((this.userProgress.achievements.size / this.achievements.length) * 100)
      },
      explorationTime: {
        minutes: Math.round(this.userProgress.explorationTime / 60000),
        formatted: this.formatTime(this.userProgress.explorationTime)
      }
    };
  }

  /**
   * Check if user has explored different continents
   */
  checkContinentExploration() {
    // Simplified continent detection based on biome types
    const continentBiomes = {
      'Africa': ['Desert', 'Tropical', 'Grassland'],
      'Asia': ['Temperate', 'Desert', 'Mountain', 'Tundra'],
      'North America': ['Temperate', 'Desert', 'Polar', 'Grassland'],
      'South America': ['Tropical', 'Mountain', 'Grassland'],
      'Europe': ['Temperate', 'Tundra'],
      'Australia': ['Desert', 'Temperate'],
      'Antarctica': ['Polar']
    };

    const exploredContinents = new Set();

    for (const [continent, biomes] of Object.entries(continentBiomes)) {
      if (biomes.some(biome => this.userProgress.biomesExplored.has(biome))) {
        exploredContinents.add(continent);
      }
    }

    return exploredContinents.size >= 3;
  }

  /**
   * Check for perfect quiz streak
   */
  checkPerfectStreak() {
    // This would require tracking recent quiz results
    // For now, simplified to check if accuracy is 100% with at least 5 questions
    return this.userProgress.totalQuestions >= 5 &&
           this.userProgress.correctAnswers === this.userProgress.totalQuestions;
  }

  /**
   * Format time duration
   */
  formatTime(milliseconds) {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);

    if (minutes === 0) {
      return `${seconds} seconds`;
    } else if (minutes < 60) {
      return `${minutes} minutes, ${seconds} seconds`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours} hours, ${remainingMinutes} minutes`;
    }
  }

  /**
   * Export learning data
   */
  exportLearningData() {
    return {
      progress: this.getProgressStats(),
      achievements: Array.from(this.userProgress.achievements).map(id =>
        this.achievements.find(a => a.id === id)
      ),
      suggestions: this.getLearningSuggestions(),
      exportDate: new Date().toISOString()
    };
  }

  /**
   * Reset progress (for testing or new users)
   */
  resetProgress() {
    this.userProgress = {
      biomesExplored: new Set(),
      quizzesTaken: new Set(),
      correctAnswers: 0,
      totalQuestions: 0,
      explorationTime: 0,
      achievements: new Set()
    };

    localStorage.removeItem('landgrid-progress');
    this.sessionStartTime = Date.now();
  }

  /**
   * Get earned achievements
   */
  getEarnedAchievements() {
    return Array.from(this.userProgress.achievements).map(id =>
      this.achievements.find(a => a.id === id)
    ).filter(Boolean);
  }

  /**
   * Get available learning paths
   */
  getLearningPaths() {
    return { ...this.learningPaths };
  }
}

